import cn from 'classnames';
import React, { ReactEventHandler, useEffect, useState } from 'react';
import { handleTrack } from '../../../utils/tracker';
import { Button } from '../../Atoms/Button';
import { Stack, StackDirection } from '../../Atoms/stack';
import { UpTick } from '../../Icons';

export interface AccordianClassNames {
  panelVisible?: string;
  detailPanel?: string;
}
export interface AccordionProps extends React.PropsWithChildren {
  header: React.ReactNode;
  className?: string;
  rightAlign?: boolean;
  onClick?: ReactEventHandler;
  classNames?: AccordianClassNames;
  isExpandedState?: boolean;
  detailPanelClasses?: string;
  accordionIconClasses?: string;
  accordianHeaderEleClasses?: string;
  iconColor?: string;
  accordionDirection?: StackDirection;
}

export const Accordion: React.FC<AccordionProps> = ({
  header,
  children,
  className = '',
  onClick,
  rightAlign = true,
  classNames = {},
  isExpandedState = false,
  detailPanelClasses,
  accordionIconClasses,
  accordianHeaderEleClasses,
  iconColor = '#9092A3',
  accordionDirection = StackDirection.TopDown,
}) => {
  // Fix hydration: Initialize with prop value to match server and client
  const [isExpanded, setIsExpanded] = useState<boolean>(isExpandedState);
  const hasContent = !!children;
  const { panelVisible = '' } = classNames;
  const accordionHeaderClasses = cn({
    ['accordion']: true,
    ['has-content']: hasContent,
    ['accordion-expanded']: isExpanded,
  });
  const accordionHeaderContent = cn({
    ['accordion-header']: true,
    ['accordion-header-expanded']: isExpanded,
    ['accordion-header-collapsed']: !isExpanded,
  });
  const accordionIcon = cn({
    ['accordion-icon']: true,
    ['accordion-icon-inverted']: isExpanded,
  });
  const accordionDetailPanel = cn({
    ['accordion-detail-panel']: true,
    ['hidden']: !isExpanded,
    ['visible']: isExpanded,
    [panelVisible]: [panelVisible.length] && isExpanded,
  });

  // Sync state when prop changes
  useEffect(() => {
    setIsExpanded(isExpandedState);
  }, [isExpandedState]);

  return (
    <Stack
      direction={accordionDirection}
      className={`${accordionHeaderClasses} ${className}`}
    >
      <Stack
        className={`w-full ${accordionHeaderContent} ${accordianHeaderEleClasses}`}
        onClick={(event) => {
          handleTrack({
            label: 'faq',
            obj: event.target,
          });
          if (hasContent) {
            setIsExpanded((prevState) => !prevState);
            onClick && onClick(event);
          }
        }}
        direction={
          rightAlign ? StackDirection.LeftToRight : StackDirection.RightToLeft
        }
      >
        <div
          className={
            'accordion-header-content font-semibold sm:text-s-14 sm:leading-6 sm:font-semibold'
          }
        >
          {header}
        </div>
        {!!hasContent && (
          <Button
            className={`${accordionIcon} ${accordionIconClasses} bg-transparent`}
            startIcon={<UpTick colorCode={iconColor} />}
          />
        )}
      </Stack>
      {!!hasContent && (
        <Stack
          className={`w-full ${accordionDetailPanel} ${detailPanelClasses}`}
        >
          {children}
        </Stack>
      )}
    </Stack>
  );
};
